import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiPlay, FiMaximize2, FiMonitor, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, Page, PageHeader, Card, Tag, EmptyState, Alert, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/trace-studio.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };

function courseIdFromUser(user) {
  if (!user?.course) return null;
  return typeof user.course === 'object' ? user.course._id : user.course;
}

// Friendly label + icon per game type (model enum: unity | html5 | iframe | external)
const TYPE_META = {
  unity: { label: 'Unity', icon: '🕹️' },
  html5: { label: 'HTML5', icon: '🎯' },
  iframe: { label: 'Web game', icon: '🧩' },
  external: { label: 'External', icon: '🔗' },
};

function typeMeta(gameType) {
  return TYPE_META[(gameType || '').toLowerCase()] || { label: gameType || 'Game', icon: '🎮' };
}

function GameThumb({ game }) {
  const meta = typeMeta(game.gameType);
  return (
    <div style={{ position: 'relative', aspectRatio: '16 / 8', background: BRAND.navy700, overflow: 'hidden' }}>
      {game.thumbnail
        ? <img src={game.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <img src={BRAND.bg.school} alt="" aria-hidden style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.5 }} />}
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(7,20,45,0.1) 0%, rgba(7,20,45,0.25) 50%, rgba(11,26,58,0.98) 100%)' }} />
      {/* gold icon chip */}
      <span aria-hidden style={{ position: 'absolute', left: 16, bottom: 14, width: 52, height: 52, borderRadius: 16, background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center', fontSize: '1.5rem', boxShadow: '0 10px 26px rgba(205,155,80,0.4)' }}>
        {meta.icon}
      </span>
      <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 6 }}>
        <Tag tone="solid"><FiPlay size={11} /> Playable</Tag>
      </div>
    </div>
  );
}

export default function GamesHub() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cid = useMemo(() => courseIdFromUser(user), [user]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!cid) {
        setGames([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/games/course/${cid}`);
        if (!cancelled) setGames(Array.isArray(data.games) ? data.games : []);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || 'Unable to load games');
          setGames([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [cid]);

  const pageProps = { hero: HERO };

  if (cid && loading) {
    return <Page {...pageProps}><LoadingSpinner message="Loading the training ground…" /></Page>;
  }

  return (
    <Page {...pageProps}>
      <PageHeader
        kicker="Training ground"
        title="Games"
        subtitle={games.length
          ? `${games.length} ${games.length === 1 ? 'challenge' : 'challenges'} ready — train your reflexes against Jason and Jackie.`
          : 'Pick a game and put your cyber skills to the test.'}
        right={!!games.length && <Tag tone="muted"><FiMonitor size={12} /> {user?.course?.name || 'Your course'}</Tag>}
      />

      {!cid && (
        <EmptyState icon="🎒" title="No course assigned" text="Ask your teacher to connect you to a course to see the games." />
      )}

      {cid && error && <Alert>{error}</Alert>}

      {cid && !error && games.length === 0 && (
        <EmptyState icon="✨" title="New games coming soon!" text="Dr. Flash is still building the next challenge. Come back later!" />
      )}

      {cid && !error && games.length > 0 && (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
          {games.map((game) => {
            const meta = typeMeta(game.gameType);
            const w = game.config?.width || 960;
            const h = game.config?.height || 600;
            return (
              <motion.div key={game._id} variants={fadeUp} whileHover={{ y: -6 }} whileTap={{ scale: 0.99 }}>
                <Link to={`/giochi/${game._id}`} style={{ display: 'block', height: '100%', textDecoration: 'none' }} className="core-game-card">
                  <Card style={{ padding: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <GameThumb game={game} />
                    <div style={{ padding: '14px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <h2 style={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1.25, margin: '0 0 6px', color: BRAND.ivory }}>{game.title}</h2>
                      <p style={{ fontSize: '0.88rem', color: BRAND.muted, lineHeight: 1.45, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {game.description || 'Ready to play? Open the game and start the mission.'}
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                        <Tag>{meta.label}</Tag>
                        <Tag tone="muted">{w}×{h}</Tag>
                        {game.config?.allowFullscreen !== false && <Tag tone="muted"><FiMaximize2 size={11} /> Fullscreen</Tag>}
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: BRAND.faint }}>Tap to open</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 999, background: GOLD_GRADIENT, color: BRAND.navy900, fontWeight: 800, fontSize: '0.84rem', whiteSpace: 'nowrap', boxShadow: '0 8px 22px rgba(205,155,80,0.3)' }}>
                          <FiPlay size={14} /> Play <FiArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      <style>{`
        .core-game-card > div { transition: border-color .2s; }
        .core-game-card:hover > div, .core-game-card:focus-visible > div { border-color: ${BRAND.gold} !important; }
        .core-game-card:focus-visible { outline: 2px solid ${BRAND.gold}; outline-offset: 4px; border-radius: 22px; }
      `}</style>
    </Page>
  );
}
